class TabManager {
    constructor(containerId, onTabChange) {
        this.container = document.getElementById(containerId);
        if (!this.container) {
            throw new Error("Invalid container id ");
        }
        // Optional hook fired once per click, after this class's own active/
        // prev/next classes are applied. Lets callers (e.g. heroBanner) drive
        // their own side effects (image/text/usp swap) from the SAME click
        // event instead of registering a second, independent listener on the
        // same tab items — two listeners meant two full DOM update passes
        // per click.
        this.onTabChange = typeof onTabChange === "function" ? onTabChange : null;
        const idPrefix = this.container.getAttribute('data-id-prefix');
        this.idPrefix = idPrefix ? idPrefix : 'tab';
        this.activeTab = this.container.querySelector(`.${this.idPrefix}__navigations--item.active-tab`);
        this.tabNavigationContainer = Array.from(this.container.querySelectorAll(`.${this.idPrefix}__navigations`));
        this.tabNavigationItems = Array.from(this.container.querySelectorAll(`.${this.idPrefix}__navigations--item`));
        this.tabPanes = Array.from(this.container.querySelectorAll(`.${this.idPrefix}__pane`));
        this.tabContentContainer = this.container.querySelector(`.${this.idPrefix}__content`);
        if (!this.tabNavigationItems || !this.tabContentContainer || !this.tabNavigationContainer) {
            throw new Error("Invalid tab structure");
        }
    }

    setTabPanePosition(){
        let maxHeightPane = this.tabPanes[0];
        this.tabPanes.forEach((el, index) => {
            if(el.offsetHeight > maxHeightPane.offsetHeight){
                maxHeightPane = el;
            }
        });
        maxHeightPane.classList.add('position-relative');
    }

    setActiveTab(tabItem) {
        if (!tabItem) {
            throw new Error("Invalid tab item provided");
        }
        if(this.activeTab){
            this.activeTab.classList.remove("active-tab");
            this.activeTab.setAttribute("aria-selected", "false");
            if (this.activeTab.previousElementSibling) {
                this.activeTab.previousElementSibling.classList.remove("prev-tab");
            }
            if (this.activeTab.nextElementSibling) {
                this.activeTab.nextElementSibling.classList.remove("next-tab");
            }
        }
        tabItem.classList.add("active-tab");
        tabItem.setAttribute("aria-selected", "true");
        // Roving tabindex: only the selected tab is in the Tab order (WAI-ARIA tabs)
        if (tabItem.getAttribute("role") === "tab") {
            this.tabNavigationItems.forEach((item) => {
                if (item.getAttribute("role") === "tab") {
                    item.setAttribute("tabindex", item === tabItem ? "0" : "-1");
                }
            });
        }
        if (tabItem.previousElementSibling) {
            tabItem.previousElementSibling.classList.add("prev-tab");
        }
        if (tabItem.nextElementSibling) {
            tabItem.nextElementSibling.classList.add("next-tab");
        }
        this.activeTab = tabItem;
    }

    handleTabClick() {
        this.tabNavigationContainer.forEach((el, index) => {
            el.addEventListener("click", (event) => {
                const tabItem = event.target.closest(`.${this.idPrefix}__navigations--item`);
                if (tabItem && !tabItem.classList.contains("active-tab")) {
                    this.setActiveTab(tabItem);
                    this.showTabContent(tabItem.id);
                    if (this.onTabChange) {
                        this.onTabChange(tabItem);
                    }
                }
            });
        });
    }

    showTabContent(tabId) {
        const contentId = `${this.idPrefix}_content-${tabId.split("-")[1]}`;
        const content = this.container.querySelector(`#${contentId}`);
        if (!content) {
            throw new Error(`Tab content with id ${contentId} not found`);
        }
        const activeContent = this.container.querySelector(`.${this.idPrefix}__pane.active-content`);
        if(activeContent){
            activeContent.classList.remove("active-content");
            if (activeContent.previousElementSibling) {
                activeContent.previousElementSibling.classList.remove("prev-tab-content");
            }
            if (activeContent.nextElementSibling) {
                activeContent.nextElementSibling.classList.remove("next-tab-content");
            }
        }
        content.classList.add("active-content");
        if (content.previousElementSibling) {
            content.previousElementSibling.classList.add("prev-tab-content");
        }
        if (content.nextElementSibling) {
            content.nextElementSibling.classList.add("next-tab-content");
        }
    }

    getCurrentTabId() {
        return this.activeTab.id;
    }

    // Keyboard support for tabs marked up with role="tab" (WCAG 2.1.1, WAI-ARIA
    // tab pattern): Tab reaches the selected tab, ←/→ (or ↑/↓) move to the
    // previous/next tab and select it, Home/End jump to the first/last, and
    // Enter/Space select the focused tab. Selection goes through click() so the
    // existing click handlers (content swap, onTabChange, analytics) all run.
    // Tab lists without role="tab" are left exactly as they were.
    handleTabKeyboard() {
        const tabs = this.tabNavigationItems.filter((item) => item.getAttribute("role") === "tab");
        if (!tabs.length) {
            return;
        }
        tabs.forEach((tab) => {
            if (!tab.hasAttribute("tabindex")) {
                tab.setAttribute("tabindex", tab.classList.contains("active-tab") ? "0" : "-1");
            }
            tab.addEventListener("keydown", (event) => {
                const index = tabs.indexOf(tab);
                let target = null;
                switch (event.key) {
                    case "ArrowRight":
                    case "ArrowDown":
                        target = tabs[(index + 1) % tabs.length];
                        break;
                    case "ArrowLeft":
                    case "ArrowUp":
                        target = tabs[(index - 1 + tabs.length) % tabs.length];
                        break;
                    case "Home":
                        target = tabs[0];
                        break;
                    case "End":
                        target = tabs[tabs.length - 1];
                        break;
                    case "Enter":
                    case " ":
                        event.preventDefault();
                        tab.click();
                        return;
                    default:
                        return;
                }
                event.preventDefault();
                target.focus();
                target.click();
            });
        });
    }

    init() {
        this.handleTabClick();
        this.handleTabKeyboard();
        this.setTabPanePosition();
    }
}


export default TabManager;
